import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Ban, Eye, Printer, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SALES_HISTORY_TICKETS_QUERY,
  REGISTER_SALE_TICKET_REPRINT_MUTATION,
} from './sales-history.gql';
import { CancelSaleDialog } from '@/modules/cashier-pos/CancelSaleDialog';
import { printOpenAccountAbonoTicket } from '@/modules/open-accounts/abono-ticket-print';
import { useCrudList } from '@/shared/crud/useCrudList';
import { SALE_ORDER_ITEM_KIND_FROM_GQL, SALE_ORDER_STATUS_FROM_GQL } from '@/shared/graphql/enums';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { useAuthStore } from '@/stores/authStore';

interface OrderCustomerSnapshot {
  first_name?: string;
  last_name?: string | null;
  phone?: string;
}

interface OrderPetSnapshot {
  name?: string;
  species?: string | null;
}

interface KitBreakdownLine {
  item_kind: string;
  item_code: string;
  item_name: string;
  quantity: string;
  unit_price_cents: string;
  total_cents: string;
}

interface SaleOrderItem {
  id: string;
  sale_order_id: string;
  line_no: number;
  item_kind: string;
  from_kit_component?: boolean;
  item_code: string;
  item_name: string;
  quantity: string;
  total_cents: string;
  kit_breakdown_lines?: KitBreakdownLine[];
}

interface SaleOrder {
  id: string;
  folio_number: string | null;
  status: string;
  paid_cents: string;
  pending_cents: string;
  customer_snapshot_json?: OrderCustomerSnapshot | string | null;
  pet_snapshot_json?: OrderPetSnapshot | string | null;
  clinical_notes: string | null;
  cashier_initial_notes: string | null;
  doctor_medical_notes: string | null;
  total_cents: string;
  items: SaleOrderItem[];
}

interface SalesHistoryPaymentMethod {
  payment_method_id: string;
  payment_method_code: string;
  payment_method_name: string;
  amount_cents: string;
}

interface AbonoTicketPayloadPayment {
  payment_method_id?: string | null;
  amount_cents?: string | number | null;
  reference_number?: string | null;
}

interface AbonoTicketPayloadConcept {
  item_code?: string | null;
  item_name?: string | null;
  quantity?: string | number | null;
  covered_cents?: string | number | null;
}

interface AbonoTicketPayload {
  open_account_folio?: string | number | null;
  paid_at?: string | null;
  amount_cents?: string | number | null;
  balance_before_cents?: string | number | null;
  balance_after_cents?: string | number | null;
  covered_concepts?: AbonoTicketPayloadConcept[] | null;
  payments?: AbonoTicketPayloadPayment[] | null;
  note?: string | null;
}

interface SalesHistoryRow {
  entry_kind: 'sale' | 'abono';
  sale_order_id: string | null;
  open_account_id: string | null;
  ticket_id: string | null;
  ticket_payload_json?: AbonoTicketPayload | string | null;
  sale_folio_number: string | null;
  ticket_folio_number: string | null;
  paid_at: string | null;
  total_cents: string;
  status: string;
  payment_method_summary: string | null;
  tutor_name: string | null;
  pet_name: string | null;
  customer_snapshot_json?: OrderCustomerSnapshot | string | null;
  pet_snapshot_json?: OrderPetSnapshot | string | null;
  payment_methods: SalesHistoryPaymentMethod[];
  sale_order: SaleOrder | null;
}

interface SalesHistoryResponse {
  salesHistoryTickets: {
    items: SalesHistoryRow[];
    page: { total: number; offset: number; limit: number; has_more: boolean };
  };
}

const TICKET_LOGO_PATH = '/totem_logo_ticket.png';
const TICKET_DEFAULT_COPIES = 2;
let cached_ticket_logo_data_uri: string | null | undefined;

export function SalesHistoryPage(): JSX.Element {
  const { t } = useTranslation();
  const list = useCrudList({ limit: 50 });
  const user_roles = useAuthStore((s) => s.user?.roles ?? []);
  const cashier_name = useAuthStore((s) => s.user?.full_name ?? 'Caja');
  const is_admin = user_roles.includes('administrator');

  const today = useMemo(() => new Date(), []);
  const default_to = useMemo(() => toInputDate(today), [today]);
  const default_from = useMemo(() => toInputDate(today), [today]);

  const [from_date, setFromDate] = useState<string>(default_from);
  const [to_date, setToDate] = useState<string>(default_to);
  const [ticket_folio, setTicketFolio] = useState<string>('');
  const [search_text, setSearchText] = useState<string>('');
  const [error_text, setErrorText] = useState<string | null>(null);
  const [detail_row, setDetailRow] = useState<SalesHistoryRow | null>(null);
  const [cancel_target_sale_order_id, setCancelTargetSaleOrderId] = useState<string | null>(null);

  useEffect(() => {
    list.setOffset(0);
  }, [from_date, to_date, ticket_folio, search_text, list.setOffset]);

  const variables = useMemo(
    () => ({
      from: toStartOfDayIso(from_date),
      to: toEndOfDayIso(to_date),
      ticket_folio: ticket_folio.trim() || null,
      search_text: search_text.trim() || null,
      offset: list.offset,
      limit: list.limit,
    }),
    [from_date, list.limit, list.offset, search_text, ticket_folio, to_date],
  );

  const { data, loading, refetch } = useQuery<SalesHistoryResponse>(SALES_HISTORY_TICKETS_QUERY, {
    variables,
    fetchPolicy: 'cache-and-network',
  });
  const [register_reprint, { loading: reprinting }] = useMutation(
    REGISTER_SALE_TICKET_REPRINT_MUTATION,
  );

  const rows = useMemo(
    () => (data?.salesHistoryTickets.items ?? []).map((row) => normalizeSalesHistoryRow(row)),
    [data?.salesHistoryTickets.items],
  );
  const page = data?.salesHistoryTickets.page ?? {
    total: 0,
    offset: 0,
    limit: list.limit,
    has_more: false,
  };

  const onClickReprint = async (row: SalesHistoryRow): Promise<void> => {
    if (!row.ticket_id) {
      setErrorText(t('sales_history.error_missing_ticket'));
      return;
    }
    const abono_payload =
      row.entry_kind === 'abono' ? parseAbonoTicketPayload(row.ticket_payload_json) : null;
    if (row.entry_kind === 'abono' && !abono_payload) {
      setErrorText(t('sales_history.error_missing_abono_payload'));
      return;
    }
    if (row.entry_kind === 'sale' && !row.sale_order) {
      setErrorText(t('sales_history.error_reprint'));
      return;
    }
    setErrorText(null);
    const idempotency_key =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      await register_reprint({
        variables: { ticket_id: row.ticket_id },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: { headers: { 'Idempotency-Key': idempotency_key } },
      });
      if (row.entry_kind === 'abono' && abono_payload) {
        await printOpenAccountAbonoTicket({
          account_folio_number:
            abono_payload.open_account_folio === null ||
            abono_payload.open_account_folio === undefined
              ? row.sale_folio_number
              : String(abono_payload.open_account_folio),
          account_id_fallback: row.open_account_id ?? row.ticket_id,
          tutor_name: normalizeHistoryLabel(row.tutor_name ?? customerLabel(row.customer_snapshot_json)),
          pet_name: normalizeHistoryLabel(row.pet_name ?? petLabel(row.pet_snapshot_json)),
          total_cents: normalizeCentsValue(abono_payload.amount_cents, row.total_cents),
          balance_before_cents: normalizeCentsValue(abono_payload.balance_before_cents, '0'),
          balance_after_cents: normalizeCentsValue(abono_payload.balance_after_cents, '0'),
          ticket_folio_number: row.ticket_folio_number,
          covered_concepts: (abono_payload.covered_concepts ?? []).map((concept) => ({
            item_code: normalizeTextValue(concept.item_code, 'ABONO'),
            item_name: normalizeTextValue(concept.item_name, 'Aportacion a cuenta'),
            quantity: normalizeTextValue(concept.quantity, '1'),
            covered_cents: normalizeCentsValue(concept.covered_cents, '0'),
          })),
          payments: buildAbonoPrintPayments(row, abono_payload),
          cashier_name,
          note: abono_payload.note?.trim() || null,
          paid_at_iso: abono_payload.paid_at ?? row.paid_at,
          mode: 'reprint',
        });
        return;
      }
      const sale_order = row.sale_order;
      if (!sale_order) {
        setErrorText(t('sales_history.error_reprint'));
        return;
      }
      await printSaleTicket(sale_order, {
        mode: 'reprint',
        cashier_name,
        ticket_number: ticketLabel(row.ticket_folio_number, row.sale_folio_number),
      });
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        setErrorText(code);
        return;
      }
      if (err instanceof Error) {
        setErrorText(err.message);
        return;
      }
      setErrorText(t('sales_history.error_reprint'));
    }
  };

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h2 className="font-display text-2xl font-bold tracking-tight">{t('sales_history.title')}</h2>
        <p className="text-muted-foreground text-sm">{t('sales_history.subtitle')}</p>
      </header>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <div className="grid gap-3 md:grid-cols-4">
            <div className="space-y-1">
              <label className="text-muted-foreground text-xs font-medium">
                {t('sales_history.from')}
              </label>
              <Input type="date" value={from_date} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-muted-foreground text-xs font-medium">
                {t('sales_history.to')}
              </label>
              <Input type="date" value={to_date} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-muted-foreground text-xs font-medium">
                {t('sales_history.ticket')}
              </label>
              <Input
                value={ticket_folio}
                onChange={(e) => setTicketFolio(e.target.value)}
                placeholder={t('sales_history.ticket_placeholder')}
              />
            </div>
            <div className="space-y-1">
              <label className="text-muted-foreground text-xs font-medium">
                {t('sales_history.search')}
              </label>
              <div className="relative">
                <Search className="text-muted-foreground pointer-events-none absolute left-2 top-2.5 h-4 w-4" />
                <Input
                  value={search_text}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="pl-8"
                  placeholder={t('sales_history.search_placeholder')}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {error_text ? (
        <div className="border-destructive/30 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm">
          {error_text}
        </div>
      ) : null}

      <div className="border-border bg-card overflow-hidden rounded-lg border">
        <table className="w-full text-sm">
          <thead className="border-border bg-muted/40 text-muted-foreground border-b text-xs uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">{t('sales_history.col_date')}</th>
              <th className="px-3 py-2 text-left">{t('sales_history.col_ticket')}</th>
              <th className="px-3 py-2 text-left">{t('sales_history.col_pet')}</th>
              <th className="px-3 py-2 text-left">{t('sales_history.col_tutor')}</th>
              <th className="px-3 py-2 text-left">{t('sales_history.col_payment_method')}</th>
              <th className="px-3 py-2 text-right">{t('sales_history.col_total')}</th>
              <th className="px-3 py-2 text-right">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-border divide-y">
            {loading ? (
              <tr>
                <td colSpan={7} className="text-muted-foreground px-4 py-8 text-center">
                  {t('common.loading')}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-muted-foreground px-4 py-8 text-center">
                  {t('sales_history.empty')}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={`${row.entry_kind}-${row.ticket_id ?? row.sale_order_id ?? row.open_account_id}`}
                  className="hover:bg-secondary/20"
                >
                  <td className="px-3 py-2">{dateTimeLabel(row.paid_at)}</td>
                  <td className="px-3 py-2">
                    <div className="font-mono">
                      {ticketLabel(row.ticket_folio_number, row.sale_folio_number)}
                    </div>
                    {row.entry_kind === 'abono' ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        <Badge variant="secondary">{t('sales_history.badge_abono')}</Badge>
                      </div>
                    ) : isOpenAccountPartialSale(row.sale_order) ? (
                      <div className="mt-1 flex flex-wrap gap-1">
                        <Badge variant="outline">{t('sales_history.badge_partial_payment')}</Badge>
                        <Badge variant="secondary">{t('sales_history.badge_open_account')}</Badge>
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">{row.pet_name ?? petLabel(row.pet_snapshot_json)}</td>
                  <td className="px-3 py-2">
                    {row.tutor_name ?? customerLabel(row.customer_snapshot_json)}
                  </td>
                  <td className="px-3 py-2">
                    {row.payment_method_summary ? (
                      <Badge variant="outline">{row.payment_method_summary}</Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">
                    {formatCentsString(row.total_cents)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setDetailRow(row);
                        }}
                        aria-label={t('sales_history.action_detail')}
                      >
                        <Eye className="h-4 w-4" aria-hidden />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          void onClickReprint(row);
                        }}
                        aria-label={t('sales_history.action_reprint')}
                        disabled={reprinting}
                      >
                        <Printer className="h-4 w-4" aria-hidden />
                      </Button>
                      {is_admin && row.sale_order_id ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setCancelTargetSaleOrderId(row.sale_order_id)}
                          aria-label={t('sales_history.action_cancel')}
                        >
                          <Ban className="text-destructive h-4 w-4" aria-hidden />
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="text-muted-foreground flex items-center justify-between text-xs">
        <span>
          {t('crud.pagination_label', {
            from: rows.length === 0 ? 0 : page.offset + 1,
            to: page.offset + rows.length,
            total: page.total,
          })}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => list.setOffset(Math.max(0, page.offset - page.limit))}
            disabled={page.offset === 0 || loading}
          >
            {t('crud.prev_page')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => list.setOffset(page.offset + page.limit)}
            disabled={!page.has_more || loading}
          >
            {t('crud.next_page')}
          </Button>
        </div>
      </div>

      {detail_row ? (
        <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <Card className="max-h-[90vh] w-full max-w-3xl overflow-auto">
            <CardHeader className="flex flex-row items-start justify-between gap-2">
              <div>
                <CardTitle className="text-base">
                  {t('sales_history.detail_title')}{' '}
                  {ticketLabel(detail_row.ticket_folio_number, detail_row.sale_folio_number)}
                </CardTitle>
                <p className="text-muted-foreground text-xs">{dateTimeLabel(detail_row.paid_at)}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    void onClickReprint(detail_row);
                  }}
                  aria-label={t('sales_history.action_reprint')}
                >
                  <Printer className="h-4 w-4" aria-hidden />
                </Button>
                {is_admin && detail_row.sale_order_id ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setCancelTargetSaleOrderId(detail_row.sale_order_id)}
                    aria-label={t('sales_history.action_cancel')}
                  >
                    <Ban className="text-destructive h-4 w-4" aria-hidden />
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setDetailRow(null)}
                  aria-label={t('common.close')}
                >
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="grid gap-2 md:grid-cols-3">
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    {t('sales_history.col_tutor')}
                  </p>
                  <p className="font-medium">{customerLabel(detail_row.customer_snapshot_json)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    {t('sales_history.col_pet')}
                  </p>
                  <p className="font-medium">{petLabel(detail_row.pet_snapshot_json)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    {t('sales_history.detail_status')}
                  </p>
                  <Badge variant="outline">
                    {detail_row.entry_kind === 'abono' || !detail_row.sale_order
                      ? t('sales_history.badge_abono')
                      : t(
                          `doctor_pos.status_${detail_row.sale_order.status}`,
                          detail_row.sale_order.status,
                        )}
                  </Badge>
                </div>
              </div>

              {detail_row.sale_order ? (
                <>
                  <div className="grid gap-2 md:grid-cols-3">
                    <div className="rounded-md border px-3 py-2">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        {t('sales_history.detail_cashier_notes')}
                      </p>
                      <p>{detail_row.sale_order.cashier_initial_notes?.trim() ?? '—'}</p>
                    </div>
                    <div className="rounded-md border px-3 py-2">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        {t('sales_history.detail_doctor_notes')}
                      </p>
                      <p>{detail_row.sale_order.doctor_medical_notes?.trim() ?? '—'}</p>
                    </div>
                    <div className="rounded-md border px-3 py-2">
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        {t('sales_history.detail_clinical_notes')}
                      </p>
                      <p>{detail_row.sale_order.clinical_notes?.trim() ?? '—'}</p>
                    </div>
                  </div>

                  <div className="rounded-md border">
                    <div className="text-muted-foreground border-b px-3 py-2 text-xs font-semibold uppercase tracking-wide">
                      {t('sales_history.detail_items')}
                    </div>
                    <ul className="divide-y text-sm">
                      {groupItemsByKit(detail_row.sale_order.items).map((group, group_idx) => (
                        <li key={`history-group-${group_idx}`} className="px-3 py-2">
                          {group.kit_label ? (
                            <p className="text-muted-foreground mb-1 text-xs font-semibold uppercase tracking-wide">
                              Kit: {group.kit_label}
                            </p>
                          ) : null}
                          <ul className="space-y-1">
                            {group.items.map((item) => (
                              <li key={item.id} className="flex items-start justify-between gap-3">
                                <span>
                                  <span className="text-muted-foreground font-mono text-xs">
                                    {item.item_code}
                                  </span>{' '}
                                  {group.kit_label
                                    ? kitComponentLabel(item.item_name)
                                    : item.item_name}
                                  {kitBreakdownDisplayRows(item).length > 0 ? (
                                    <span className="text-muted-foreground mt-1 block text-xs">
                                      {kitBreakdownDisplayRows(item).join(' | ')}
                                    </span>
                                  ) : null}
                                </span>
                                <span className="font-mono tabular-nums">
                                  {formatCentsString(item.total_cents)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : null}

              <div className="rounded-md border">
                <div className="text-muted-foreground border-b px-3 py-2 text-xs font-semibold uppercase tracking-wide">
                  {t('sales_history.detail_payments')}
                </div>
                <ul className="divide-y">
                  {detail_row.payment_methods.length === 0 ? (
                    <li className="text-muted-foreground px-3 py-2">—</li>
                  ) : (
                    detail_row.payment_methods.map((payment) => (
                      <li
                        key={`${payment.payment_method_id}-${payment.amount_cents}`}
                        className="flex items-center justify-between px-3 py-2"
                      >
                        <span>{payment.payment_method_name}</span>
                        <span className="font-mono tabular-nums">{formatCentsString(payment.amount_cents)}</span>
                      </li>
                    ))
                  )}
                </ul>
              </div>

              <div className="flex items-center justify-between text-sm font-semibold">
                <span>{t('sales_history.col_total')}</span>
                <span className="font-mono tabular-nums">{formatCentsString(detail_row.total_cents)}</span>
              </div>
              {detail_row.sale_order && isOpenAccountPartialSale(detail_row.sale_order) ? (
                <div className="border-warning/40 bg-warning/15 rounded-md border px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{t('sales_history.badge_partial_payment')}</Badge>
                    <Badge variant="secondary">{t('sales_history.badge_open_account')}</Badge>
                  </div>
                  <p className="mt-2">
                    {t('sales_history.partial_paid_label')}{' '}
                    <span className="font-mono tabular-nums">
                      {formatCentsString(detail_row.sale_order.paid_cents)}
                    </span>
                  </p>
                  <p>
                    {t('sales_history.partial_pending_label')}{' '}
                    <span className="font-mono tabular-nums">
                      {formatCentsString(detail_row.sale_order.pending_cents)}
                    </span>
                  </p>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      ) : null}

      {cancel_target_sale_order_id ? (
        <CancelSaleDialog
          sale_order_id={cancel_target_sale_order_id}
          on_close={() => setCancelTargetSaleOrderId(null)}
          on_error={setErrorText}
          on_done={async () => {
            setCancelTargetSaleOrderId(null);
            if (detail_row?.sale_order_id === cancel_target_sale_order_id) setDetailRow(null);
            await refetch();
          }}
        />
      ) : null}
    </div>
  );
}

function normalizeSalesHistoryRow(row: SalesHistoryRow): SalesHistoryRow {
  return {
    ...row,
    status:
      SALE_ORDER_STATUS_FROM_GQL[row.status as keyof typeof SALE_ORDER_STATUS_FROM_GQL] ??
      row.status,
    customer_snapshot_json: parseSnapshot<OrderCustomerSnapshot>(row.customer_snapshot_json),
    pet_snapshot_json: parseSnapshot<OrderPetSnapshot>(row.pet_snapshot_json),
    ticket_payload_json: parseSnapshot<AbonoTicketPayload>(row.ticket_payload_json),
    sale_order: row.sale_order ? normalizeSaleOrder(row.sale_order) : null,
  };
}

function normalizeSaleOrder(order: SaleOrder): SaleOrder {
  return {
    ...order,
    status:
      SALE_ORDER_STATUS_FROM_GQL[order.status as keyof typeof SALE_ORDER_STATUS_FROM_GQL] ??
      order.status,
    customer_snapshot_json: parseSnapshot<OrderCustomerSnapshot>(order.customer_snapshot_json),
    pet_snapshot_json: parseSnapshot<OrderPetSnapshot>(order.pet_snapshot_json),
    items: order.items.map((item) => ({
      ...item,
      item_kind:
        SALE_ORDER_ITEM_KIND_FROM_GQL[
          item.item_kind as keyof typeof SALE_ORDER_ITEM_KIND_FROM_GQL
        ] ?? item.item_kind,
    })),
  };
}

function parseSnapshot<T>(value: T | string | null | undefined): T | null {
  if (!value) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function parseAbonoTicketPayload(
  value: AbonoTicketPayload | string | null | undefined,
): AbonoTicketPayload | null {
  if (!value) return null;
  if (typeof value !== 'string') return value;
  return parseSnapshot<AbonoTicketPayload>(value);
}

function buildAbonoPrintPayments(
  row: SalesHistoryRow,
  payload: AbonoTicketPayload,
): Array<{
  payment_method_name: string;
  amount_cents: string;
  reference_number: string | null;
}> {
  const payment_method_names = new Map(
    row.payment_methods.map((payment) => [payment.payment_method_id, payment.payment_method_name]),
  );
  const payload_payments = payload.payments ?? [];
  if (payload_payments.length === 0) {
    return row.payment_methods.map((payment) => ({
      payment_method_name: payment.payment_method_name,
      amount_cents: payment.amount_cents,
      reference_number: null,
    }));
  }
  return payload_payments.map((payment, index) => ({
    payment_method_name:
      (payment.payment_method_id
        ? payment_method_names.get(payment.payment_method_id) ?? null
        : null) ??
      row.payment_methods[index]?.payment_method_name ??
      'Pago',
    amount_cents: normalizeCentsValue(payment.amount_cents, '0'),
    reference_number: payment.reference_number?.trim() || null,
  }));
}

function normalizeCentsValue(value: string | number | null | undefined, fallback: string): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(Math.trunc(value));
  if (typeof value === 'string' && /^-?\d+$/.test(value.trim())) return value.trim();
  return fallback;
}

function normalizeTextValue(value: string | number | null | undefined, fallback: string): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > 0) return trimmed;
  }
  return fallback;
}

function normalizeHistoryLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed !== '-' ? trimmed : null;
}

function customerLabel(snap: OrderCustomerSnapshot | string | null | undefined): string {
  if (typeof snap === 'string') return customerLabel(parseSnapshot<OrderCustomerSnapshot>(snap));
  if (!snap) return '-';
  const first = snap.first_name ?? '';
  const last = snap.last_name ?? '';
  const name = [first, last].filter(Boolean).join(' ').trim();
  if (name) return name;
  return snap.phone ?? '-';
}

function petLabel(snap: OrderPetSnapshot | string | null | undefined): string {
  if (typeof snap === 'string') return petLabel(parseSnapshot<OrderPetSnapshot>(snap));
  if (!snap?.name) return '-';
  return snap.species ? `${snap.name} - ${snap.species}` : snap.name;
}

function toInputDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toStartOfDayIso(value: string): string {
  const parsed = parseInputDate(value);
  if (!parsed) return new Date().toISOString();
  parsed.setHours(0, 0, 0, 0);
  return parsed.toISOString();
}

function toEndOfDayIso(value: string): string {
  const parsed = parseInputDate(value);
  if (!parsed) return new Date().toISOString();
  parsed.setHours(23, 59, 59, 999);
  return parsed.toISOString();
}

function parseInputDate(value: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split('-').map((part) => Number(part));
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function dateTimeLabel(iso: string | null): string {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    return date.toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

function ticketLabel(ticket_folio: string | null, sale_folio: string | null): string {
  if (ticket_folio) return `T-${ticket_folio}`;
  if (sale_folio) return `V-${sale_folio}`;
  return '-';
}

function extractKitLabel(item_name: string): string | null {
  const idx = item_name.indexOf(' - ');
  if (idx <= 0) return null;
  return item_name.slice(0, idx).trim() || null;
}

function kitComponentLabel(item_name: string): string {
  const idx = item_name.indexOf(' - ');
  if (idx <= 0) return item_name;
  return item_name.slice(idx + 3).trim() || item_name;
}

function kitBreakdownDisplayRows(item: SaleOrderItem): string[] {
  if (item.item_kind !== 'kit') return [];
  return (item.kit_breakdown_lines ?? []).map(
    (line) =>
      `${line.item_code} - ${line.item_name} ${line.quantity} x ${formatCentsString(
        line.unit_price_cents,
      )} = ${formatCentsString(line.total_cents)}`,
  );
}

function groupItemsByKit(
  items: readonly SaleOrderItem[],
): { kit_label: string | null; items: SaleOrderItem[] }[] {
  const groups: { kit_label: string | null; items: SaleOrderItem[] }[] = [];
  for (const item of items) {
    const looks_like_kit_component =
      item.from_kit_component === true || item.item_name.includes(' - ');
    if (looks_like_kit_component) {
      const label = extractKitLabel(item.item_name) ?? 'Kit';
      const last = groups[groups.length - 1];
      if (last?.kit_label === label) {
        last.items.push(item);
        continue;
      }
      groups.push({ kit_label: label, items: [item] });
      continue;
    }
    groups.push({ kit_label: null, items: [item] });
  }
  return groups;
}

async function printSaleTicket(
  order: SaleOrder,
  options: {
    copies?: number;
    mode: 'ticket' | 'reprint';
    cashier_name: string;
    ticket_number?: string | null;
  },
  target_window?: Window | null,
): Promise<void> {
  if (typeof window === 'undefined') return;
  const normalized_options = { ...options, copies: options.copies ?? TICKET_DEFAULT_COPIES };
  const logo_data_uri = await resolveTicketLogoDataUri();
  const printable = buildTicketHtml(order, normalized_options, logo_data_uri);
  const popup = target_window ?? window.open('', '_blank', 'width=420,height=720');
  if (!popup) {
    const current = document.body.innerHTML;
    document.body.innerHTML = printable;
    window.print();
    document.body.innerHTML = current;
    window.location.reload();
    return;
  }
  popup.document.open();
  popup.document.write(printable);
  popup.document.close();
  await waitForPopupReady(popup);
  await waitForPopupImages(popup);
  popup.addEventListener(
    'afterprint',
    () => {
      popup.close();
    },
    { once: true },
  );
  popup.focus();
  popup.print();
}

function buildTicketHtml(
  order: SaleOrder,
  options: {
    copies: number;
    mode: 'ticket' | 'reprint';
    cashier_name: string;
    ticket_number?: string | null;
  },
  logo_data_uri: string | null,
): string {
  const now_label = escapeHtml(dateTimeLabel(new Date().toISOString()));
  const normalized_ticket_number = options.ticket_number?.trim();
  const ticket_number = escapeHtml(
    normalized_ticket_number && normalized_ticket_number.length > 0
      ? normalized_ticket_number
      : ticketLabel(null, order.folio_number ?? order.id.slice(0, 8)),
  );
  const cashier = escapeHtml(options.cashier_name);
  const ticket_kind = options.mode === 'reprint' ? 'REIMPRESION' : 'TICKET';
  const logo_markup = logo_data_uri
    ? `<img class="logo" src="${escapeHtml(logo_data_uri)}" alt="Totem" />`
    : '<p class="logo-fallback">TOTEM</p>';
  const rows = groupItemsByKit(order.items)
    .map((group) => {
      const header = group.kit_label
        ? `<tr><td colspan="3" class="group">${escapeHtml(group.kit_label)}</td></tr>`
        : '';
      const body = group.items
        .map((item) => {
          const breakdown = kitBreakdownDisplayRows(item);
          const breakdown_rows =
            item.item_kind === 'kit' && breakdown.length > 0
              ? breakdown
                  .map(
                    (entry) => `
        <tr class="kit-sub">
          <td>  - ${escapeHtml(entry)}</td>
          <td class="num"></td>
          <td class="num"></td>
        </tr>`,
                  )
                  .join('')
              : '';
          return `
        <tr>
          <td>${escapeHtml(group.kit_label ? kitComponentLabel(item.item_name) : item.item_name)}</td>
          <td class="num">${escapeHtml(item.quantity)}</td>
          <td class="num">${escapeHtml(formatCentsString(item.total_cents))}</td>
        </tr>${breakdown_rows}`;
        })
        .join('');
      return `${header}${body}`;
    })
    .join('');
  const paid_cents = safeHistoryBigInt(order.paid_cents);
  const pending_cents = safeHistoryBigInt(order.pending_cents);
  const has_open_account_pending = isOpenAccountPartialSale(order);
  const payment_summary_markup = has_open_account_pending
    ? `
      <p class="line"><strong>Pago parcial en caja:</strong> ${escapeHtml(formatCentsString(paid_cents.toString()))}</p>
      <p class="line"><strong>Pendiente en cuenta abierta:</strong> ${escapeHtml(formatCentsString(pending_cents.toString()))}</p>
      <p class="line"><strong>Estatus:</strong> Cuenta abierta con saldo pendiente</p>`
    : `<p class="line"><strong>Pagado:</strong> ${escapeHtml(formatCentsString(paid_cents.toString()))}</p>`;
  const copy = `
    <section class="ticket">
      <p class="kind">${ticket_kind}</p>
      <div class="logo-wrap">
        ${logo_markup}
      </div>
      <p class="brand">Hospital Veterinario de Especialidades Totem</p>
      <p class="line">Direccion: Prolongacion Cuauhtemoc 224. Huizache 1, Durango, Mexico</p>
      <p class="line">Telefono: 618 112 3896</p>
      <p class="line">Fecha: ${now_label}</p>
      <p class="line">No de Ticket: ${ticket_number}</p>
      <p class="line">Le Atendio: ${cashier}</p>
      <hr />
      <p class="line">Nombre cliente: Usuario General</p>
      <hr />
      <table>
        <thead><tr><th>Concepto</th><th class="num">Cant.</th><th class="num">Total</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <hr />
      <p class="total">Total ${escapeHtml(formatCentsString(order.total_cents))}</p>
      ${payment_summary_markup}
      <div class="footer">
        <p class="footer-main">CONSERVE ESTE COMPROBANTE</p>
        <p class="footer-sub">ES INDISPENSABLE PARA CUALQUIER ACLARACION</p>
        <p class="footer-thanks">GRACIAS POR TU VISITA!</p>
        <p class="footer-sub">NO OLVIDES SEGUIRNOS EN NUESTRAS REDES SOCIALES</p>
        <p class="footer-social">@hospital.totem</p>
        <p class="footer-main">DUDAS O ACLARACIONES:</p>
        <p class="footer-contact">618 112 3896</p>
      </div>
    </section>`;
  return `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Ticket ${ticket_number}</title>
        <style>
          @page { size: 80mm auto; margin: 0; }
          html, body { margin: 0; width: 80mm; }
          body { font-family: Arial, Helvetica, sans-serif; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .ticket { width: 80mm; box-sizing: border-box; padding: 2.2mm 2.2mm 2mm; page-break-after: always; break-after: page; }
          .ticket:last-child { page-break-after: auto; break-after: auto; }
          p { margin: 2px 0; font-size: 11px; line-height: 1.2; }
          .kind { margin: 0 0 4px; text-align: center; font-size: 12px; font-weight: 800; letter-spacing: 0.4px; }
          .logo-wrap { text-align: center; margin: 0 0 4px; }
          .logo { width: 52mm; max-width: 100%; height: auto; }
          .logo-fallback { margin: 0; font-size: 16px; font-weight: 800; letter-spacing: 0.8px; }
          .brand { text-align: center; font-weight: 700; font-size: 11px; margin-bottom: 3px; }
          .line { margin: 1px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 10px; }
          th { text-align: left; border-bottom: 1px solid #111; padding-bottom: 2px; }
          td { padding: 2px 0; vertical-align: top; }
          .group { font-weight: 700; padding-top: 8px; }
          .kit-sub td { font-size: 9px; color: #555; }
          .num { text-align: right; white-space: nowrap; }
          .total { font-size: 13px; font-weight: 800; text-align: right; margin-top: 4px; }
          .footer { text-align: center; margin-top: 8px; border-top: 1px solid #111; padding-top: 6px; }
          .footer-main { font-weight: 800; font-size: 11px; margin: 2px 0; }
          .footer-sub { font-size: 9px; margin: 2px 0; }
          .footer-thanks { font-weight: 900; font-size: 18px; line-height: 1; margin: 4px 0; }
          .footer-social { font-weight: 700; font-size: 14px; line-height: 1; margin: 3px 0; }
          .footer-contact { font-weight: 700; font-size: 15px; line-height: 1; margin: 3px 0; }
          hr { border: 0; border-top: 1px solid #111; margin: 5px 0; }
          @media print {
            html, body { width: 80mm; }
            .ticket { page-break-after: always; break-after: page; }
            .ticket:last-child { page-break-after: auto; break-after: auto; }
          }
        </style>
      </head>
      <body>${Array.from({ length: options.copies }, () => copy).join('')}</body>
    </html>`;
}

async function resolveTicketLogoDataUri(): Promise<string | null> {
  if (cached_ticket_logo_data_uri !== undefined) return cached_ticket_logo_data_uri;
  try {
    const response = await fetch(TICKET_LOGO_PATH, { cache: 'force-cache' });
    if (!response.ok) {
      cached_ticket_logo_data_uri = null;
      return null;
    }
    const blob = await response.blob();
    cached_ticket_logo_data_uri = await blobToDataUri(blob);
    return cached_ticket_logo_data_uri;
  } catch {
    cached_ticket_logo_data_uri = null;
    return null;
  }
}

function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('logo_read_failed'));
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('logo_read_failed'));
    };
    reader.readAsDataURL(blob);
  });
}

async function waitForPopupReady(popup: Window): Promise<void> {
  if (popup.document.readyState === 'complete') return;
  await Promise.race([
    new Promise<void>((resolve) => popup.addEventListener('load', () => resolve(), { once: true })),
    delay(1200),
  ]);
}

async function waitForPopupImages(popup: Window): Promise<void> {
  const images = Array.from(popup.document.images ?? []);
  if (images.length === 0) return;
  await Promise.race([
    Promise.all(
      images.map((image) =>
        image.complete
          ? Promise.resolve()
          : new Promise<void>((resolve) => {
              image.addEventListener('load', () => resolve(), { once: true });
              image.addEventListener('error', () => resolve(), { once: true });
            }),
      ),
    ).then(() => undefined),
    delay(1400),
  ]);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function safeHistoryBigInt(value: string | null | undefined): bigint {
  try {
    if (!value || !/^-?\d+$/.test(value)) return 0n;
    return BigInt(value);
  } catch {
    return 0n;
  }
}

function isOpenAccountPartialSale(
  order: { status: string; pending_cents: string } | null,
): boolean {
  if (!order) return false;
  const pending_cents = safeHistoryBigInt(order.pending_cents);
  return (
    pending_cents > 0n && (order.status === 'partially_paid' || order.status === 'open_account')
  );
}
